import { computed, watch, toValue, type MaybeRefOrGetter } from 'vue';
import { storeToRefs } from 'pinia';
import useAnnotationsStore from './annotations-store';

const useHighlights = (
  contentId: MaybeRefOrGetter<string | null | undefined>,
  profileId?: MaybeRefOrGetter<string | undefined>,
  options = { autoLoad: true },
) => {
  const store = useAnnotationsStore();
  const { create, update, remove, isFetching } = store;
  const { annotations } = storeToRefs(store);

  const highlights = computed(() => {
    const id = toValue(contentId);
    return Object.values(annotations.value).filter(
      (a) => (a.type === 'highlight' || a.type === 'underline') && a.contentId === id,
    );
  });

  // Reactive loading state specific to these types/contentId
  const isLoading = computed(() => {
    const id = toValue(contentId);
    return isFetching([`highlight:${id}`, `underline:${id}`]);
  });

  const load = async () => {
    const id = toValue(contentId);
    const pId = toValue(profileId);
    if (!id) {
      return Promise.reject();
    }

    return Promise.all([store.load('highlight', id, pId), store.load('underline', id, pId)]);
  };

  watch(
    () => toValue(contentId),
    (newId) => {
      if (options.autoLoad && newId && highlights.value.length === 0) {
        load();
      }
    },
    { immediate: true },
  );

  return {
    highlights,
    isLoading,
    load,
    create,
    update,
    remove,
  };
};

export default useHighlights;
